import { useCallback, useEffect, useState, useMemo } from 'react';
import { useTranslation } from '../translations';

/**
 * Хук для копирования текста в буфер обмена с отображением состояния кнопки.
 *
 * @param {string} [copyValue="XXX-XXX-XXXX"] - значение текста, которое нужно будет скопировать.
 * @param {string} [defaultValue] - стандартное значение текста по умолчанию.
 * @param {number} [delay=2000] - задержка (мс) возврата текста в исходное состояние после копирования.
 * @param {function} [onCopy] - функция после успешного копирования.
 *
 * @return {[string, (value?: string) => void, boolean]}
 *  - [0] text — текущее состояние текста.
 *  - [1] setText — callback состояния текста, который вызывает копирование текста copyValue.
 * Триггерит таймер выключения (отжатия кнопки)
 *  - [2] disabled — значение для блокировки кнопки (true, если setText уже использовался и не прошло
 * delay времени), чтобы избавиться от спама копирования.
 */
export const useCopy = (copyValue = 'XXX-XXX-XXXX', defaultValue, delay = 2000, onCopy) => {
  const { t } = useTranslation();

  const startValue = useMemo(() => defaultValue || t('other.copyPromoCode'), [defaultValue, t]);

  const [text, setText] = useState(startValue);
  const [hasCopied, setHasCopied] = useState(false);

  const copy = useCallback(
    /**
     * Устанавливает текст и флаг копирования.
     *
     * @param {string} [value] - Новый текст. Если не передан, будет установлен перевод "promoCodeCopied".
     */
    (value) => {
      setHasCopied(true);
      setText(typeof value === 'string' ? value : t('other.promoCodeCopied'));
    },
    [t],
  );

  useEffect(() => {
    if (!hasCopied) return;

    const timeoutId = setTimeout(() => {
      setText(startValue);
      setHasCopied(false);
    }, delay);

    (async () => {
      try {
        if ('clipboard' in navigator && copyValue) {
          await navigator.clipboard.writeText(copyValue);
          onCopy();
        } else {
          console.warn('Clipboard API недоступен');
        }
      } catch (err) {
        console.error('Ошибка при копировании:', err);
      }
    })();

    return () => clearTimeout(timeoutId);
  }, [hasCopied, startValue, copyValue, delay]);

  return [text, copy, hasCopied];
};
